<script setup lang="ts">
  import { computed } from 'vue';
  import {
    DatePicker,
    IconImage,
    FilterModal,
    ThemeProvider,
    NumberStepperInput,
    SelectInput,
  } from '@/shared/ui';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { usePermittedMembers } from '@/entities/User';
  import {
    DocumentRevisionStatus,
    DocumentRevisionStatusShortLabels,
    formatCollaboratorNameAndRegistration,
  } from '@/shared/lib';
  import { useDocumentFilterModal } from '@/features/Document';
  import { useFilterStore } from '@/entities/Filter';
  import type { FilterOptions } from '@/entities/Document';

  const props = defineProps<{
    currentFilters?: FilterOptions;
    revisionStatus: string;
    type: string;
    date: string | null;
    revision: number;
    user: string;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'update:filter', filters: FilterOptions): void;
    (e: 'update:selectedDate', date: Date | null): void;
    (e: 'clear-filters'): void;
  }>();

  const { userOptions: memberOptions, fetchUsers } = usePermittedMembers({
    requiredPermissions: [
      { action: 'send', subject: 'Document' },
      { action: 'manageApproval', subject: 'Homologation' },
    ],
    matchMode: 'any',
    includeInactiveMembers: true,
  });
  const filterStore = useFilterStore();

  const {
    formData,
    elaborationDate,
    approvalDate,
    isElaborationDateOpen,
    isApprovalDateOpen,
    formatDate,
    clearFilters,
    applyFilters,
    loadFilters,
  } = useDocumentFilterModal(
    emit,
    fetchUsers,
    (props.currentFilters || filterStore.filters) as Partial<FilterOptions>
  );

  // Os mesmos três rótulos da coluna Status, para o filtro e a tabela falarem a mesma língua.
  const statusOptions = [
    DocumentRevisionStatus.PENDING_APPROVAL,
    DocumentRevisionStatus.PENDING_VERIFICATION,
    DocumentRevisionStatus.VERIFIED,
  ].map((value) => ({ label: DocumentRevisionStatusShortLabels[value], value }));

  const usersList = computed(() =>
    memberOptions.value.map((member) => ({
      id: member.id,
      name: formatCollaboratorNameAndRegistration(member.name, member.registrationNumber, {
        labelWhenNameMissing: 'Nome não disponível',
      }),
    }))
  );

  const handleClearFilters = () => {
    clearFilters();
    emit('clear-filters');
  };
</script>

<template>
  <FilterModal
    @close="emit('close')"
    @clear-filters="handleClearFilters"
    @apply-filters="applyFilters"
  >
    <template #content>
      <ThemeProvider class="overflow-auto">
        <div class="px-6 space-y-4">
          <div class="py-8 px-4">
            <h4 class="font-medium text-gray-07 pb-4">
              Status
            </h4>
            <div class="flex flex-col gap-2">
              <button
                v-for="option in statusOptions"
                :key="option.value"
                :class="[
                  'py-2 rounded-lg w-40',
                  formData.revisionStatus === option.value
                    ? 'bg-(--theme-secondary) text-white'
                    : 'text-gray-07',
                ]"
                @click="
                  formData.revisionStatus =
                    formData.revisionStatus === option.value ? '' : option.value
                "
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div class="flex py-12 px-4 gap-4 border-y border-gray-02">
            <div class="w-1/2">
              <h4 class="font-medium text-gray-07 pb-4">
                Data de Elaboração
              </h4>
              <button
                class="flex items-center bg-white-01 px-8 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) font-medium w-full"
                @click="isElaborationDateOpen = true"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendário"
                  class="h-5 w-5"
                />
                <span class="grow text-center">{{
                  elaborationDate !== null ? formatDate(elaborationDate) : 'Selecione uma data'
                }}</span>
              </button>
              <DatePicker
                v-model:selected-date="elaborationDate"
                v-model:is-open="isElaborationDateOpen"
                class="my-auto"
                @apply="isElaborationDateOpen = false"
                @cancel="isElaborationDateOpen = false"
              />
            </div>

            <div class="w-1/2">
              <h4 class="font-medium text-gray-07 pb-4">
                Data de Aprovação
              </h4>
              <button
                class="flex items-center bg-white-01 px-8 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) font-medium w-full"
                @click="isApprovalDateOpen = true"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendário"
                  class="h-5 w-5"
                />
                <span class="grow text-center">{{
                  approvalDate !== null ? formatDate(approvalDate) : 'Selecione uma data'
                }}</span>
              </button>
              <DatePicker
                v-model:selected-date="approvalDate"
                v-model:is-open="isApprovalDateOpen"
                class="my-auto"
                @apply="isApprovalDateOpen = false"
                @cancel="isApprovalDateOpen = false"
              />
            </div>
          </div>

          <div class="py-12 px-4">
            <div class="flex gap-4">
              <div class="w-1/4">
                <h4 class="font-medium text-gray-07 mb-2">
                  Revisões
                </h4>
                <NumberStepperInput
                  :model-value="formData.revision ?? null"
                  :min="0"
                  :step="1"
                  input-class="w-full pr-9"
                  wrapper-class="w-full"
                  @update:model-value="formData.revision = $event ?? undefined"
                />
              </div>

              <div class="w-3/4">
                <h4 class="font-medium text-gray-07 mb-2">
                  Elaboração/Aprovação
                </h4>
                <SelectInput
                  :model-value="formData.elaborationApproval ?? ''"
                  :options="usersList"
                  :use-teleport="true"
                  label="Selecione um colaborador"
                  max-height="12rem"
                  @update:model-value="formData.elaborationApproval = $event"
                />
              </div>
            </div>
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
